import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft,
  Sparkles,
  Download,
  Trash2,
  Send,
  Tags,
  ShoppingBag,
  Image as ImageIcon,
  Upload,
  X,
  ChevronDown,
  Loader2,
  Check,
  Globe,
  Palette,
} from "lucide-react";
import { useState, useRef, useCallback } from "react";
import { cn } from "@/lib/utils";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useLocation, useParams } from "wouter";
import type { CreativeGroup, Creative, Brand } from "@shared/schema";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

type QuickSelectMode = "brand" | "product" | "competitor" | null;

export default function CreativeCanvas() {
  const { id } = useParams<{ id: string }>();
  const groupId = Number(id);
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();

  const [promptText, setPromptText] = useState("");
  const [quickSelectOpen, setQuickSelectOpen] = useState<QuickSelectMode>(null);
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [productUrl, setProductUrl] = useState("");
  const [competitorAdFile, setCompetitorAdFile] = useState<File | null>(null);
  const [competitorAdPreview, setCompetitorAdPreview] = useState<string | null>(null);
  const [referenceFiles, setReferenceFiles] = useState<File[]>([]);
  const [referencePreviews, setReferencePreviews] = useState<string[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const competitorInputRef = useRef<HTMLInputElement>(null);
  const referenceInputRef = useRef<HTMLInputElement>(null);

  const { data: group } = useQuery<CreativeGroup>({
    queryKey: ["/api/creative-groups", groupId],
    queryFn: async () => {
      const res = await fetch(`/api/creative-groups/${groupId}`);
      if (!res.ok) throw new Error("Group not found");
      return res.json();
    },
    enabled: !!groupId,
  });

  const { data: creatives = [] } = useQuery<Creative[]>({
    queryKey: ["/api/creative-groups", groupId, "creatives"],
    queryFn: async () => {
      const res = await fetch(`/api/creative-groups/${groupId}/creatives`);
      return res.json();
    },
    enabled: !!groupId,
  });

  const { data: brands = [] } = useQuery<Brand[]>({
    queryKey: ["/api/brands"],
  });

  const handleCompetitorUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCompetitorAdFile(file);
    const reader = new FileReader();
    reader.onload = () => setCompetitorAdPreview(reader.result as string);
    reader.readAsDataURL(file);
  }, []);

  const handleReferenceUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setReferenceFiles((prev) => [...prev, ...files]);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => setReferencePreviews((prev) => [...prev, reader.result as string]);
      reader.readAsDataURL(file);
    });
  }, []);

  const removeReference = (index: number) => {
    setReferenceFiles((prev) => prev.filter((_, i) => i !== index));
    setReferencePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleGenerate = () => {
    if (!promptText.trim() && !selectedBrand) return;
    setIsGenerating(true);
    setTimeout(() => {
      setIsGenerating(false);
    }, 3000);
  };

  const generatedCreatives = creatives.filter((c) => c.imageUrl);

  return (
    <Layout>
      <div className="flex flex-col h-[calc(100vh-8rem)] relative">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Button
              data-testid="button-back-to-groups"
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-gray-400 hover:text-gray-900"
              onClick={() => navigate("/create-ads")}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 data-testid="text-canvas-title" className="text-xl font-bold text-gray-900 tracking-tight">
                {group?.name || "Creative Canvas"}
              </h1>
              <p className="text-xs text-gray-500">
                {generatedCreatives.length} creative{generatedCreatives.length !== 1 ? "s" : ""} generated
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {selectedBrand && (
              <Badge variant="outline" className="gap-1.5 py-1.5 px-3 bg-primary/5 border-primary/20 text-primary">
                <Palette className="h-3 w-3" />
                {selectedBrand.name}
              </Badge>
            )}
            {productUrl && (
              <Badge variant="outline" className="gap-1.5 py-1.5 px-3 bg-blue-50 border-blue-200 text-blue-700">
                <Globe className="h-3 w-3" />
                Product linked
              </Badge>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto pb-48">
          {generatedCreatives.length > 0 ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {generatedCreatives.map((creative) => (
                <div
                  key={creative.id}
                  data-testid={`card-creative-${creative.id}`}
                  className="group relative rounded-xl bg-white shadow-sm border border-gray-100 overflow-hidden transition-all hover:shadow-lg"
                >
                  <div className="aspect-square bg-gray-100 relative">
                    {creative.imageUrl ? (
                      <img src={creative.imageUrl} alt="Generated" className="h-full w-full object-cover" />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center">
                        <Loader2 className="h-8 w-8 text-gray-300 animate-spin" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <Button size="sm" variant="secondary" className="gap-1.5 shadow-lg">
                        <Download className="h-3.5 w-3.5" /> Save
                      </Button>
                      <Button size="icon" variant="secondary" className="h-8 w-8 shadow-lg">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  {creative.prompt && (
                    <div className="p-3">
                      <p className="text-xs text-gray-500 line-clamp-2">{creative.prompt}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center max-w-md">
                <div className="h-24 w-24 bg-gradient-to-br from-primary/10 to-primary/5 rounded-3xl flex items-center justify-center mx-auto mb-6">
                  <Sparkles className="h-12 w-12 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Ready to Create</h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  Select your brand, add product details, and describe your ad below. AI will generate 4 creative variations for you.
                </p>
                <div className="flex items-center justify-center gap-3 mt-6">
                  <div className="flex items-center gap-1.5 text-xs text-gray-400">
                    <Tags className="h-3.5 w-3.5" /> Brand
                  </div>
                  <div className="h-1 w-1 rounded-full bg-gray-300" />
                  <div className="flex items-center gap-1.5 text-xs text-gray-400">
                    <ShoppingBag className="h-3.5 w-3.5" /> Product
                  </div>
                  <div className="h-1 w-1 rounded-full bg-gray-300" />
                  <div className="flex items-center gap-1.5 text-xs text-gray-400">
                    <ImageIcon className="h-3.5 w-3.5" /> Competitor Ad
                  </div>
                </div>
              </div>
            </div>
          )}

          {isGenerating && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="rounded-xl bg-white shadow-sm border border-gray-100 overflow-hidden">
                  <div className="aspect-square bg-gray-50 flex items-center justify-center relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent -skew-x-12 translate-x-[-150%] animate-[shimmer_2s_infinite]" />
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="h-8 w-8 text-primary/40 animate-spin" />
                      <span className="text-[10px] text-gray-400 font-medium">Generating...</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#F3F4F6] via-[#F3F4F6] to-transparent pt-8 pb-0">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-4 mx-auto max-w-5xl">
            {(selectedBrand || productUrl || competitorAdPreview || referencePreviews.length > 0) && (
              <div className="flex flex-wrap items-center gap-2 mb-3 pb-3 border-b border-gray-100">
                {selectedBrand && (
                  <div className="flex items-center gap-2 bg-primary/5 border border-primary/20 rounded-lg px-3 py-1.5">
                    <div className="h-5 w-5 rounded bg-white flex items-center justify-center overflow-hidden border border-gray-200">
                      {selectedBrand.logo ? (
                        <img src={selectedBrand.logo} alt="" className="h-full w-full object-contain" />
                      ) : (
                        <span className="text-[8px] font-bold text-primary">{selectedBrand.name.charAt(0)}</span>
                      )}
                    </div>
                    <span className="text-xs font-medium text-primary">{selectedBrand.name}</span>
                    <button onClick={() => setSelectedBrand(null)} className="text-primary/50 hover:text-primary">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}
                {productUrl && (
                  <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5">
                    <Globe className="h-3.5 w-3.5 text-blue-600" />
                    <span className="text-xs font-medium text-blue-700 max-w-[150px] truncate">{productUrl}</span>
                    <button onClick={() => setProductUrl("")} className="text-blue-400 hover:text-blue-600">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}
                {competitorAdPreview && (
                  <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                    <div className="h-5 w-5 rounded overflow-hidden">
                      <img src={competitorAdPreview} alt="" className="h-full w-full object-cover" />
                    </div>
                    <span className="text-xs font-medium text-amber-700">Competitor Ad</span>
                    <button
                      onClick={() => {
                        setCompetitorAdFile(null);
                        setCompetitorAdPreview(null);
                      }}
                      className="text-amber-400 hover:text-amber-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}
                {referencePreviews.map((preview, i) => (
                  <div key={i} className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-1.5">
                    <div className="h-5 w-5 rounded overflow-hidden">
                      <img src={preview} alt="" className="h-full w-full object-cover" />
                    </div>
                    <span className="text-xs font-medium text-green-700">Asset {i + 1}</span>
                    <button onClick={() => removeReference(i)} className="text-green-400 hover:text-green-600">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-end gap-3">
              <div className="flex items-center gap-1.5">
                <Popover open={quickSelectOpen === "brand"} onOpenChange={(open) => setQuickSelectOpen(open ? "brand" : null)}>
                  <PopoverTrigger asChild>
                    <Button
                      data-testid="button-select-brand"
                      variant="outline"
                      size="sm"
                      className={cn(
                        "h-9 gap-1.5 text-xs rounded-xl border-gray-200 hover:border-primary/30 hover:bg-primary/5 transition-all",
                        selectedBrand && "border-primary/30 bg-primary/5 text-primary"
                      )}
                    >
                      <Tags className="h-3.5 w-3.5" />
                      Brand
                      {selectedBrand && <Check className="h-3 w-3" />}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-2" align="start">
                    <div className="space-y-1">
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-2 py-1">Select Brand Identity</p>
                      {brands.length === 0 ? (
                        <p className="text-xs text-gray-400 px-2 py-3 text-center">No brands yet. Create one first.</p>
                      ) : (
                        brands.map((brand) => (
                          <button
                            key={brand.id}
                            data-testid={`button-brand-option-${brand.id}`}
                            className={cn(
                              "w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-gray-50 text-left transition-colors",
                              selectedBrand?.id === brand.id && "bg-primary/5"
                            )}
                            onClick={() => {
                              setSelectedBrand(brand);
                              setQuickSelectOpen(null);
                            }}
                          >
                            <div className="h-8 w-8 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden border border-gray-200">
                              {brand.logo ? (
                                <img src={brand.logo} alt="" className="h-full w-full object-contain" />
                              ) : (
                                <span className="text-xs font-bold text-primary">{brand.name.charAt(0)}</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{brand.name}</p>
                              {brand.industry && (
                                <p className="text-[10px] text-gray-400">{brand.industry}</p>
                              )}
                            </div>
                            {selectedBrand?.id === brand.id && (
                              <Check className="h-4 w-4 text-primary flex-shrink-0" />
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  </PopoverContent>
                </Popover>

                <Popover open={quickSelectOpen === "product"} onOpenChange={(open) => setQuickSelectOpen(open ? "product" : null)}>
                  <PopoverTrigger asChild>
                    <Button
                      data-testid="button-select-product"
                      variant="outline"
                      size="sm"
                      className={cn(
                        "h-9 gap-1.5 text-xs rounded-xl border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all",
                        productUrl && "border-blue-300 bg-blue-50 text-blue-700"
                      )}
                    >
                      <ShoppingBag className="h-3.5 w-3.5" />
                      Product
                      {productUrl && <Check className="h-3 w-3" />}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-3" align="start">
                    <div className="space-y-2">
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Product URL</p>
                      <Input
                        data-testid="input-product-url"
                        placeholder="https://yourstore.com/product"
                        value={productUrl}
                        onChange={(e) => setProductUrl(e.target.value)}
                        className="text-sm"
                      />
                      <p className="text-[10px] text-gray-400">AI will analyze this product page for context.</p>
                      <Button
                        size="sm"
                        className="w-full bg-primary hover:bg-primary/90 text-white text-xs"
                        onClick={() => setQuickSelectOpen(null)}
                        disabled={!productUrl.trim()}
                      >
                        Set Product
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>

                <Popover open={quickSelectOpen === "competitor"} onOpenChange={(open) => setQuickSelectOpen(open ? "competitor" : null)}>
                  <PopoverTrigger asChild>
                    <Button
                      data-testid="button-select-competitor"
                      variant="outline"
                      size="sm"
                      className={cn(
                        "h-9 gap-1.5 text-xs rounded-xl border-gray-200 hover:border-amber-300 hover:bg-amber-50 transition-all",
                        competitorAdPreview && "border-amber-300 bg-amber-50 text-amber-700"
                      )}
                    >
                      <ImageIcon className="h-3.5 w-3.5" />
                      Competitor Ad
                      {competitorAdPreview && <Check className="h-3 w-3" />}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-3" align="start">
                    <div className="space-y-3">
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Upload Competitor Ad</p>
                      {competitorAdPreview ? (
                        <div className="relative rounded-lg overflow-hidden border border-gray-200">
                          <img src={competitorAdPreview} alt="Competitor" className="w-full h-32 object-cover" />
                          <button
                            className="absolute top-2 right-2 bg-white/90 rounded-full p-1 shadow-sm hover:bg-white"
                            onClick={() => {
                              setCompetitorAdFile(null);
                              setCompetitorAdPreview(null);
                            }}
                          >
                            <X className="h-3 w-3 text-gray-600" />
                          </button>
                        </div>
                      ) : (
                        <button
                          className="w-full border-2 border-dashed border-gray-200 rounded-lg p-6 flex flex-col items-center gap-2 hover:bg-gray-50 hover:border-amber-300 transition-all"
                          onClick={() => competitorInputRef.current?.click()}
                        >
                          <Upload className="h-6 w-6 text-gray-400" />
                          <span className="text-xs text-gray-500">Click to upload ad image</span>
                        </button>
                      )}
                      <input
                        ref={competitorInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleCompetitorUpload}
                      />
                      <p className="text-[10px] text-gray-400">Upload a competitor's ad to clone its style.</p>
                    </div>
                  </PopoverContent>
                </Popover>

                <Button
                  data-testid="button-upload-assets"
                  variant="outline"
                  size="sm"
                  className={cn(
                    "h-9 gap-1.5 text-xs rounded-xl border-gray-200 hover:border-green-300 hover:bg-green-50 transition-all",
                    referencePreviews.length > 0 && "border-green-300 bg-green-50 text-green-700"
                  )}
                  onClick={() => referenceInputRef.current?.click()}
                >
                  <Upload className="h-3.5 w-3.5" />
                  Assets
                  {referencePreviews.length > 0 && (
                    <span className="bg-green-600 text-white text-[9px] rounded-full h-4 w-4 flex items-center justify-center">
                      {referencePreviews.length}
                    </span>
                  )}
                </Button>
                <input
                  ref={referenceInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleReferenceUpload}
                />
              </div>

              <div className="flex-1 relative">
                <Textarea
                  data-testid="input-prompt"
                  placeholder="Describe your ad creative... e.g., 'Minimalist summer sale poster with bold typography and pastel gradients'"
                  value={promptText}
                  onChange={(e) => setPromptText(e.target.value)}
                  className="min-h-[44px] max-h-[120px] resize-none pr-12 rounded-xl border-gray-200 focus-visible:ring-primary/20 text-sm"
                  rows={1}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleGenerate();
                    }
                  }}
                />
              </div>

              <Button
                data-testid="button-generate"
                className={cn(
                  "h-11 w-11 rounded-xl p-0 transition-all",
                  promptText.trim() || selectedBrand
                    ? "bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20"
                    : "bg-gray-100 text-gray-400"
                )}
                onClick={handleGenerate}
                disabled={isGenerating || (!promptText.trim() && !selectedBrand)}
              >
                {isGenerating ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Send className="h-5 w-5" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
